/**
 * Monitor de Performance para Settings Cache
 *
 * Implementa sistema de monitoramento e métricas conforme especificado
 * na documentação do design para eliminar loops infinitos.
 */

import React, { useState, useCallback } from 'react';
import {
  Typography,
  Grid,
  Card,
  CardContent,
  CardHeader,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Button,
  Box,
  Divider,
  Collapse,
  IconButton
} from '@material-ui/core';
import {
  TrendingUp as TrendingUpIcon,
  Memory as MemoryIcon,
  Speed as SpeedIcon,
  Storage as StorageIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Computer as MonitorIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon
} from '@material-ui/icons';
import { i18n } from '../../translate/i18n';
import { makeStyles } from '@material-ui/core/styles';
import useSettingsPerformanceMetrics from '../../hooks/useSettingsPerformanceMetrics';

const useStyles = makeStyles((theme) => ({
  settingContainer: {
    padding: '20px',
    border: '1px solid',
    borderColor: theme.palette.type === 'dark' ? '#444' : '#e0e0e0',
    borderRadius: '8px',
    backgroundColor: theme.palette.type === 'dark' ? '#252525' : '#ffffff',
    transition: 'all 0.2s',
    marginBottom: '16px'
  },
  sectionHeader: {
    display: "flex",
    alignItems: "center",
    marginBottom: '8px'
  },
  sectionIcon: {
    marginRight: '8px',
    color: theme.palette.primary.main,
  },
  card: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column'
  },
  cardContent: {
    flexGrow: 1
  },
  metricValue: {
    fontSize: '2rem',
    fontWeight: 'bold',
    marginBottom: theme.spacing(0.5)
  },
  metricLabel: {
    fontSize: '0.875rem',
    color: theme.palette.text.secondary
  },
  goodMetric: {
    color: theme.palette.success.main
  },
  warningMetric: {
    color: theme.palette.warning.main
  },
  badMetric: {
    color: theme.palette.error.main
  },
  statusChip: {
    fontSize: '0.75rem',
    height: '20px',
    marginTop: theme.spacing(1)
  },
  listItem: {
    paddingLeft: 0,
    paddingRight: 0
  }
}));

const SettingsPerformanceMonitor = () => {
  const classes = useStyles();
  const {
    metrics,
    calculatedStats,
    hasRecentLoops,
    loopReport,
    resetMetrics
  } = useSettingsPerformanceMetrics();

  const [refreshCount, setRefreshCount] = useState(0);
  const [lastRefresh] = useState(new Date());
  const [expandedSections, setExpandedSections] = useState({
    metrics: true,
    status: true
  });

  // Atualizar timestamp a cada refresh manual
  const handleRefresh = useCallback(() => {
    setRefreshCount(prev => prev + 1);
  }, []);

  const toggleSection = useCallback((section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  }, []);

  const getMetricStatus = useCallback((value, type) => {
    switch (type) {
      case 'hitRate':
        if (value >= 80) return 'good';
        if (value >= 60) return 'warning';
        return 'bad';
      case 'memoryUsage':
        if (value <= 100) return 'good';
        if (value <= 500) return 'warning';
        return 'bad';
      case 'responseTime':
        if (value <= 100) return 'good';
        if (value <= 500) return 'warning';
        return 'bad';
      default:
        return 'good';
    }
  }, []);

  const getStatusColor = useCallback((status) => {
    switch (status) {
      case 'good':
        return 'primary';
      case 'warning':
        return 'secondary';
      case 'bad':
        return 'default';
      default:
        return 'primary';
    }
  }, []);

  const getStatusLabel = useCallback((status) => {
    switch (status) {
      case 'good':
        return i18n.t('performanceMonitor.status.excellent');
      case 'warning':
        return i18n.t('performanceMonitor.status.warning');
      case 'bad':
        return i18n.t('performanceMonitor.status.critical');
      default:
        return i18n.t('performanceMonitor.status.unknown');
    }
  }, []);

  const hitRateStatus = getMetricStatus(calculatedStats.hitRate, 'hitRate');
  const responseTimeStatus = getMetricStatus(calculatedStats.averageResponseTime, 'responseTime');

  return (
    <Box>
      {/* Header */}
      <Box className={classes.settingContainer}>
        <Box className={classes.sectionHeader}>
          <MonitorIcon className={classes.sectionIcon} />
          <Typography variant="h6" style={{ fontWeight: 600 }}>
            {i18n.t('performanceMonitor.title')}
          </Typography>
        </Box>
        <Typography variant="body2" color="textSecondary" style={{ marginBottom: '16px' }}>
          {i18n.t('performanceMonitor.description')}
        </Typography>

        <Grid container spacing={1} style={{ marginBottom: '16px' }}>
          <Grid item>
            <Chip
              icon={hasRecentLoops ? <ErrorIcon /> : <CheckCircleIcon />}
              label={hasRecentLoops ? i18n.t('performanceMonitor.status.loopsDetected') : i18n.t('performanceMonitor.status.healthy')}
              color={hasRecentLoops ? 'default' : 'primary'}
              variant={hasRecentLoops ? 'outlined' : 'default'}
              className={classes.statusChip}
            />
          </Grid>
          <Grid item>
            <Chip
              icon={<TrendingUpIcon />}
              label={`${calculatedStats.hitRate}% ${i18n.t('performanceMonitor.status.hitRate')}`}
              color={getStatusColor(hitRateStatus)}
              className={classes.statusChip}
            />
          </Grid>
          <Grid item>
            <Chip
              icon={<SpeedIcon />}
              label={`${metrics.requestCount} ${i18n.t('performanceMonitor.status.requests')}`}
              color="default"
              variant="outlined"
              className={classes.statusChip}
            />
          </Grid>
        </Grid>

        <Box display="flex" gap={1}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<RefreshIcon />}
            onClick={handleRefresh}
          >
            {i18n.t('performanceMonitor.actions.refresh')} ({refreshCount})
          </Button>
          <Button
            variant="contained"
            color="secondary"
            size="small"
            onClick={resetMetrics}
          >
            {i18n.t('performanceMonitor.actions.resetMetrics')}
          </Button>
        </Box>
      </Box>

      {/* Métricas de Performance */}
      <Card style={{ marginBottom: '16px' }}>
        <CardHeader
          avatar={<TrendingUpIcon />}
          title={i18n.t('performanceMonitor.sections.metrics')}
          action={
            <IconButton onClick={() => toggleSection('metrics')}>
              {expandedSections.metrics ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          }
        />
        <Collapse in={expandedSections.metrics}>
          <CardContent>
            <Grid container spacing={3}>
              {/* Hit Rate */}
              <Grid item xs={12} md={3}>
                <Card className={classes.card}>
                  <CardContent className={classes.cardContent}>
                    <Box display="flex" alignItems="center" mb={1}>
                      <TrendingUpIcon color="primary" />
                      <Typography variant="h6" style={{ marginLeft: 8 }}>
                        {i18n.t('performanceMonitor.metrics.hitRate')}
                      </Typography>
                    </Box>
                    <Typography
                      className={`${classes.metricValue} ${classes[hitRateStatus + 'Metric']}`}
                    >
                      {calculatedStats.hitRate}%
                    </Typography>
                    <Typography className={classes.metricLabel}>
                      {i18n.t('performanceMonitor.metrics.hitRateDesc')}
                    </Typography>
                    <Box mt={1}>
                      <Chip
                        size="small"
                        label={getStatusLabel(hitRateStatus)}
                        color={getStatusColor(hitRateStatus)}
                        className={classes.statusChip}
                      />
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              {/* Total de Requisições */}
              <Grid item xs={12} md={3}>
                <Card className={classes.card}>
                  <CardContent className={classes.cardContent}>
                    <Box display="flex" alignItems="center" mb={1}>
                      <SpeedIcon color="primary" />
                      <Typography variant="h6" style={{ marginLeft: 8 }}>
                        {i18n.t('performanceMonitor.metrics.requests')}
                      </Typography>
                    </Box>
                    <Typography className={`${classes.metricValue} ${classes.goodMetric}`}>
                      {metrics.requestCount}
                    </Typography>
                    <Typography className={classes.metricLabel}>
                      {i18n.t('performanceMonitor.metrics.requestsDesc')}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              {/* Tempo de Resposta */}
              <Grid item xs={12} md={3}>
                <Card className={classes.card}>
                  <CardContent className={classes.cardContent}>
                    <Box display="flex" alignItems="center" mb={1}>
                      <MemoryIcon color="primary" />
                      <Typography variant="h6" style={{ marginLeft: 8 }}>
                        Resposta
                      </Typography>
                    </Box>
                    <Typography
                      className={`${classes.metricValue} ${classes[responseTimeStatus + 'Metric']}`}
                    >
                      {calculatedStats.averageResponseTime}ms
                    </Typography>
                    <Typography className={classes.metricLabel}>
                      Tempo médio de resposta
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              {/* Cache Hits/Misses */}
              <Grid item xs={12} md={3}>
                <Card className={classes.card}>
                  <CardContent className={classes.cardContent}>
                    <Box display="flex" alignItems="center" mb={1}>
                      <StorageIcon color="primary" />
                      <Typography variant="h6" style={{ marginLeft: 8 }}>
                        Cache
                      </Typography>
                    </Box>
                    <Typography className={`${classes.metricValue} ${classes.goodMetric}`}>
                      {metrics.cacheHits}/{metrics.cacheMisses}
                    </Typography>
                    <Typography className={classes.metricLabel}>
                      Hits / Misses
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </CardContent>
        </Collapse>
      </Card>

      {/* Status do Sistema */}
      <Card>
        <CardHeader
          avatar={<CheckCircleIcon />}
          title={i18n.t('performanceMonitor.sections.systemStatus')}
          action={
            <IconButton onClick={() => toggleSection('status')}>
              {expandedSections.status ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          }
        />
        <Collapse in={expandedSections.status}>
          <CardContent>
            <Divider style={{ margin: '16px 0' }} />

            <List>
              <ListItem className={classes.listItem}>
                <ListItemIcon>
                  {hasRecentLoops ? <ErrorIcon color="error" /> : <CheckCircleIcon color="primary" />}
                </ListItemIcon>
                <ListItemText
                  primary={i18n.t('performanceMonitor.system.loopDetection')}
                  secondary={
                    hasRecentLoops
                      ? `⚠️ ${loopReport.totalLoops} ${i18n.t('performanceMonitor.system.loopsDetected')}`
                      : `✅ ${i18n.t('performanceMonitor.system.noLoops')}`
                  }
                />
              </ListItem>

              <ListItem className={classes.listItem}>
                <ListItemIcon>
                  <TrendingUpIcon color="primary" />
                </ListItemIcon>
                <ListItemText
                  primary={i18n.t('performanceMonitor.system.cacheEfficiency')}
                  secondary={`${calculatedStats.cacheEfficiency} - ${calculatedStats.hitRate}% ${i18n.t('performanceMonitor.system.hits')}`}
                />
              </ListItem>

              <ListItem className={classes.listItem}>
                <ListItemIcon>
                  <SpeedIcon color="primary" />
                </ListItemIcon>
                <ListItemText
                  primary={i18n.t('performanceMonitor.system.requestsPerHour')}
                  secondary={`${calculatedStats.requestsPerHour} ${i18n.t('performanceMonitor.system.requestsLastHour')}`}
                />
              </ListItem>
            </List>
          </CardContent>
        </Collapse>
      </Card>

      <Box mt={2}>
        <Typography variant="body2" color="textSecondary">
          {i18n.t('performanceMonitor.lastUpdate')}: {lastRefresh.toLocaleTimeString()} •
          {i18n.t('performanceMonitor.lastReset')}: {new Date(metrics.lastReset).toLocaleString()}
        </Typography>
      </Box>
    </Box>
  );
};

export default SettingsPerformanceMonitor;